import {
  ComponentProps,
  CSSProperties,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  ComboBox,
  ComboBoxStateContext,
  Input,
  Key,
  ListBox,
  ListBoxItem,
  Popover,
} from "react-aria-components";

import {
  Checkbox,
  Spinner,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import styles from "./MultiSelectCombobox.module.css";

export interface MultiSelectComboboxItem {
  id: string;
  label: string;
}

const ICON_SIZE = 20;
const POPOVER_OFFSET = 5;

// React Aria commits (toggles) the focused option on Tab while the menu is
// open — wrong for multi-select, so swallow the Tab keydown and close instead.
function TabSafeInput(props: ComponentProps<typeof Input>) {
  const state = useContext(ComboBoxStateContext);
  return (
    <Input
      {...props}
      onKeyDownCapture={(event) => {
        if (event.key === "Tab") {
          event.stopPropagation();
          state?.close();
        }
      }}
    />
  );
}

interface OptionProps<T extends MultiSelectComboboxItem> {
  item: T;
  isDisabled: boolean;
  isSelected: boolean;
  renderItemIcon?: (props: { item: T; isSelected: boolean }) => ReactNode;
  size: "compact" | "medium" | "large";
  tooltip?: string;
}

function MultiSelectOption<T extends MultiSelectComboboxItem>({
  item,
  isDisabled,
  isSelected,
  renderItemIcon,
  size,
  tooltip,
}: OptionProps<T>) {
  const content = (
    <div className={styles.itemContent}>
      <span aria-hidden={true} className={styles.checkWrapper}>
        <Checkbox
          excludeFromTabOrder
          isDisabled={isDisabled}
          isSelected={isSelected}
          name={item.id}
          onChange={() => null}
        />
      </span>
      {renderItemIcon?.({ item, isSelected })}
      <Text className={styles.itemLabel} type="Body2">
        {item.label}
      </Text>
    </div>
  );

  return (
    <ListBoxItem
      className={styles.item}
      data-size={size}
      id={item.id}
      textValue={item.label}
    >
      {tooltip ? (
        <Tooltip allowMultiline content={tooltip} placement="top">
          <TooltipFocusableTrigger>{content}</TooltipFocusableTrigger>
        </Tooltip>
      ) : (
        content
      )}
    </ListBoxItem>
  );
}

interface Props<T extends MultiSelectComboboxItem> {
  /** Accessibility label for the combobox. Supply when no external label is present. */
  ariaLabel?: string;
  /** Accessible label for the built-in clear-search button. The button renders only while the input has text. */
  clearSearchLabel?: string;
  /** Ids of items that cannot be toggled (e.g. already assigned). Include them in `selectedIds` to render them checked. */
  disabledIds?: string[];
  /** Message shown in the open menu when `items` is empty. When omitted, the menu hides instead. */
  emptyState?: ReactNode;
  /** Tooltip content for an item, typically explaining why it is disabled. Return undefined for no tooltip. */
  getItemTooltip?: (item: T) => string | undefined;
  /** HTML id for the input element, e.g. from a `Field` render prop. */
  inputId?: string;
  inputValue: string;
  isDisabled?: boolean;
  /** Shows a spinner in place of the search icon. */
  isBusy?: boolean;
  items: T[];
  menuAriaLabel: string;
  onInputValueChange: (value: string) => void;
  /** Called with the item whose selection state the user toggled. */
  onToggleItem: (item: T) => void;
  placeholder: string;
  /** Optional decoration rendered between the checkbox and the label, e.g. an avatar. */
  renderItemIcon?: (props: { item: T; isSelected: boolean }) => ReactNode;
  selectedIds: string[];
  size?: "compact" | "medium" | "large";
}

/**
 * Multi-select search combobox whose menu renders in a portaled popover, so it
 * overlays surrounding UI (e.g. modals) instead of growing their scroll area.
 */
// design-contract: bridges a Zinnia gap (ENT-2588) — BetaMultiTypeahead's chips
// can't be removed; delete this component once the DS ships a chip-less variant.
export function MultiSelectCombobox<T extends MultiSelectComboboxItem>({
  ariaLabel,
  clearSearchLabel,
  disabledIds,
  emptyState,
  getItemTooltip,
  inputId,
  inputValue,
  isBusy = false,
  isDisabled = false,
  items,
  menuAriaLabel,
  onInputValueChange,
  onToggleItem,
  placeholder,
  renderItemIcon,
  selectedIds,
  size = "medium",
}: Props<T>) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const selectedKeys = useMemo(() => new Set(selectedIds), [selectedIds]);
  const disabledKeys = useMemo(() => new Set(disabledIds), [disabledIds]);

  // Standalone popovers don't get RAC's --trigger-width, so measure the
  // input wrapper ourselves (same approach as Zinnia's Typeahead).
  const [menuWidth, setMenuWidth] = useState<string>();
  useEffect(() => {
    const node = triggerRef.current;
    if (!node) return;
    const update = () => {
      setMenuWidth(`${node.getBoundingClientRect().width}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // React Aria only repositions the portaled menu on window resize/scroll,
  // so nudge it when the selection list below the field moves the input.
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [selectedIds.length]);

  // The ComboBox reports the full new selection; translate it into per-item
  // toggle callbacks so async search results can't drop existing selections.
  const handleChange = (keys: Key[]) => {
    const next = new Set(keys.map(String));
    for (const item of items) {
      if (selectedKeys.has(item.id) !== next.has(item.id)) {
        onToggleItem(item);
      }
    }
  };

  const showMenu = items.length > 0 || Boolean(emptyState);

  return (
    <div
      className={styles.root}
      data-custom-component="account-management::multi-select-combobox"
    >
      <ComboBox<T, "multiple">
        allowsEmptyCollection
        aria-label={ariaLabel}
        disabledKeys={disabledIds}
        inputValue={inputValue}
        isDisabled={isDisabled}
        items={items}
        menuTrigger="focus"
        onChange={handleChange}
        onInputChange={onInputValueChange}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            onInputValueChange("");
          }
        }}
        selectionMode="multiple"
        shouldFocusWrap
        value={selectedIds}
      >
        <div className={styles.inputWrapper} data-size={size} ref={triggerRef}>
          <div className={styles.iconLeft} aria-hidden={true}>
            {isBusy ? <Spinner /> : <Icon name="navSearch" size={ICON_SIZE} />}
          </div>
          <TabSafeInput
            aria-busy={isBusy || undefined}
            autoComplete="off"
            className={styles.input}
            data-size={size}
            id={inputId}
            placeholder={placeholder}
          />
          {clearSearchLabel && inputValue && (
            <Button
              aria-label={clearSearchLabel}
              className={styles.clearButton}
              data-size={size}
              onPress={() => onInputValueChange("")}
              // Don't inherit the ComboBox toggle-button slot behavior.
              slot={null}
            >
              <Icon name="formXCircle" size={ICON_SIZE} />
            </Button>
          )}
        </div>
        <Popover
          className={styles.popover}
          data-content={showMenu ? "has-content" : "no-content"}
          offset={POPOVER_OFFSET}
          style={{ "--menu-width": menuWidth } as CSSProperties}
          triggerRef={triggerRef}
        >
          <ListBox<T>
            aria-label={menuAriaLabel}
            className={styles.listBox}
            dependencies={[selectedKeys, renderItemIcon, getItemTooltip]}
            renderEmptyState={() => (
              <div className={styles.emptyState}>
                <Text type="Body2">{emptyState}</Text>
              </div>
            )}
          >
            {(item) => (
              <MultiSelectOption
                isDisabled={disabledKeys.has(item.id)}
                isSelected={selectedKeys.has(item.id)}
                item={item}
                renderItemIcon={renderItemIcon}
                size={size}
                tooltip={getItemTooltip?.(item)}
              />
            )}
          </ListBox>
        </Popover>
      </ComboBox>
    </div>
  );
}
